/**
 * Pending Approval Status Page
 * Shows users their registration status while waiting for admin approval
 * Uses Supabase direct queries to check pending_registrations
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Clock, CheckCircle2, XCircle, AlertCircle, RefreshCw, Loader2 } from 'lucide-react';
import { pendingRegistrations } from '../lib/db';

export default function PendingApproval() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const checkStatus = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await pendingRegistrations.getByEmail(email);
      if (fetchError) throw fetchError;
      if (!data) {
        setError('No registration found for this email address');
        setStatus(null);
      } else {
        setStatus(data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to check status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusIcon = () => {
    if (!status) return <Clock className="w-12 h-12 text-slate-400" />;
    switch (status.status) {
      case 'approved':
        return <CheckCircle2 className="w-12 h-12 text-emerald-500" />;
      case 'rejected':
        return <XCircle className="w-12 h-12 text-red-500" />;
      default:
        return <Clock className="w-12 h-12 text-amber-500" />;
    }
  };

  const getStatusText = () => {
    if (!status) return null;
    switch (status.status) {
      case 'approved':
        return {
          title: 'Approved!',
          desc: 'Your registration has been approved. You can now sign in.',
        };
      case 'rejected':
        return {
          title: 'Registration Declined',
          desc: 'Unfortunately, your registration was not approved.',
        };
      default:
        return {
          title: 'Pending Review',
          desc: 'Your registration is being reviewed by an administrator.',
        };
    }
  };

  const statusInfo = getStatusText();

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card
        className="w-full max-w-md border-slate-200 shadow-lg rounded-2xl"
        data-testid="pending-approval-card"
      >
        <CardHeader className="space-y-1 pb-4">
          <CardTitle
            className="text-2xl font-semibold text-slate-800 text-center"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Registration Status
          </CardTitle>
          <CardDescription className="text-base text-slate-600 text-center">
            Check the status of your registration
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4" data-testid="error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e: any) => setEmail(e.target.value)}
                className="h-11 border-slate-300"
                data-testid="email-input"
              />
              <Button
                onClick={checkStatus}
                disabled={loading}
                variant="outline"
                className="h-11 px-4"
                data-testid="check-status-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </div>
            {status && (
              <div className="text-center py-6" data-testid="status-display">
                <div className="mb-4">{getStatusIcon()}</div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">{statusInfo?.title}</h3>
                <p className="text-slate-600 text-sm mb-4">{statusInfo?.desc}</p>
                {status.status === 'approved' && (
                  <Link to="/app/login">
                    <Button
                      className="bg-primary-600 hover:bg-primary-700"
                      data-testid="go-to-login-btn"
                    >
                      Go to Login
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/app/login"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
              data-testid="back-to-login-link"
            >
              Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
